import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { EMPTY, Subject, catchError, debounceTime, switchMap, takeUntil } from 'rxjs';
import { CompanyModalComponent } from '../company-modal/company-modal.component';
import { CityDto, CompanyCatalogFilters, CompanyInfo, CountryDto, SectorDto } from '../shared/dto/models-dto';
import { FooterComponent } from '../shared/footer/footer.component';
import { HeaderComponent } from '../shared/header/header.component';
import { AllCompaniesService } from './all-companies.service';

interface DimensionRating {
  name: string;
  label: string;
  rating: number;
  hoverRating: number;
}

interface CompanyFilter {
  title: string;
  name: 'dimension' | 'inclusionIndex';
  open: boolean;
  dimensions?: DimensionRating[];
  value?: number;
  min?: number;
  max?: number;
}

@Component({
  selector: 'app-all-companies',
  standalone: true,
  imports: [
    HeaderComponent,
    FooterComponent,
    CommonModule,
    FormsModule,
    MatIconModule,
    MatTooltipModule,
    MatSelectModule,
    MatCheckboxModule,
    TranslateModule
  ],
  templateUrl: './all-companies.component.html',
  styleUrl: './all-companies.component.css'
})
export class AllCompaniesComponent implements OnInit, OnDestroy {

  companies: CompanyInfo[] = [];
  filteredCompanies: CompanyInfo[] = [];
  searchTerm = '';
  expandedCompanyId: number | null = null;
  selectedCountryId: number | null = null;
  selectedCities: number[] = [0];
  previousSelectedCities: number[] = [0];
  selectedSectorId: number | null = null;
  badgeFilter = false;

  countries: CountryDto[] = [];
  cities: CityDto[] = [];
  filteredCities: CityDto[] = [];
  sectors: SectorDto[] = [];

  currentPage = 1;
  readonly itemsPerPage = 8;
  totalPages = 0;
  totalElements = 0;

  get firstDisplayedElement(): number {
    return (this.currentPage - 1) * this.itemsPerPage + 1;
  }

  get lastDisplayedElement(): number {
    const previousPagesElements = (this.currentPage - 1) * this.itemsPerPage;
    return Math.min(previousPagesElements + this.filteredCompanies.length, this.totalElements);
  }

  companyId = 1;
  selectedFile: File | null = null;
  logoBase64: string | null = null;
  error: string | null = null;

  private readonly reloadCompanies$ = new Subject<void>();
  private readonly searchChanges$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();

  dimensionLabels = [
    { key: 'Leadership and Organizational Readiness', label: 'Leadership', code: '{{leadership.title' },
    { key: 'Integration and Meaningful Engagement', label: 'Integration', code: '{{integration.title' },
    { key: 'Mentorship and Skill Development', label: 'Mentorship', code: '{{mentorship.title' },
    { key: 'Cultural and Social Inclusiveness', label: 'Cultural', code: '{{cultural.title' },
    { key: 'Outcome and Continuous Improvement', label: 'Outcome', code: '{{outcome.title' }
  ];

  filters: CompanyFilter[] = [
    {
      title: 'inclusion-index.dimension',
      name: 'dimension',
      open: false,
      dimensions: [
        { name: 'leadership', label: 'Leadership', rating: 0, hoverRating: 0 },
        { name: 'bintegration', label: 'Integration', rating: 0, hoverRating: 0 },
        { name: 'mentorship', label: 'Mentorship', rating: 0, hoverRating: 0 },
        { name: 'cultural', label: 'Cultural', rating: 0, hoverRating: 0 },
        { name: 'outcome', label: 'Outcome', rating: 0, hoverRating: 0 }
      ]
    },
    {
      title: 'inclusion-index.score',
      name: 'inclusionIndex',
      open: false,
      value: 1,
      min: 1,
      max: 5
    }
  ];

  constructor(
    private allService: AllCompaniesService,
    private router: Router,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.reloadCompanies$.pipe(
      switchMap(() => this.allService.getCompanies(this.buildFilters()).pipe(
        catchError(err => {
          console.error('Errore caricamento aziende', err);
          this.error = 'Errore caricamento aziende';
          return EMPTY;
        })
      )),
      takeUntil(this.destroy$)
    ).subscribe(response => {
      this.companies = response.content;
      this.filteredCompanies = response.content;
      this.currentPage = response.page + 1;
      this.totalPages = response.totalPages;
      this.totalElements = response.totalElements;
      this.error = null;
    });

    this.searchChanges$.pipe(
      debounceTime(250),
      takeUntil(this.destroy$)
    ).subscribe(() => this.applyFilters());

    this.allService.getCountries().pipe(takeUntil(this.destroy$)).subscribe({
      next: data => this.countries = data,
      error: err => console.error('Errore countries', err)
    });

    this.allService.getCities().pipe(takeUntil(this.destroy$)).subscribe({
      next: data => this.cities = data,
      error: err => console.error('Errore cities', err)
    });

    this.allService.getSectors().pipe(takeUntil(this.destroy$)).subscribe({
      next: data => this.sectors = data,
      error: err => console.error('Errore sectors', err)
    });

    this.reloadCompanies$.next();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.reloadCompanies$.complete();
    this.searchChanges$.complete();
  }

  toggleAccordion(index: number): void {
    this.filters[index].open = !this.filters[index].open;
  }

  onCountryChange(): void {
    this.selectedCountryId = this.selectedCountryId ?? null;
    this.selectedCities = [0];
    this.previousSelectedCities = [0];
    this.filteredCities = this.selectedCountryId == null
      ? []
      : this.cities.filter(city => city.country?.id === this.selectedCountryId);
    this.applyFilters();
  }

  onCitiesChange(): void {
    const hasAllCities = this.selectedCities.includes(0);
    const hasOtherCities = this.selectedCities.some(cityId => cityId !== 0);
    const hadAllCities = this.previousSelectedCities.includes(0);

    if (hasAllCities && hasOtherCities) {
      this.selectedCities = hadAllCities
        ? this.selectedCities.filter(cityId => cityId !== 0)
        : [0];
    } else if (this.selectedCities.length === 0) {
      this.selectedCities = [0];
    }

    this.previousSelectedCities = [...this.selectedCities];
    this.applyFilters();
  }

  onSearchChange(): void {
    this.searchChanges$.next();
  }

  getSelectedCityName(cityId: number | undefined): string {
    return this.cities.find(city => city.id === cityId)?.name ?? '';
  }

  getDimensionScore(company: CompanyInfo, key: string): string {
    const score = company.dimensionScores?.[key];
    return score !== undefined ? score.toFixed(2) : 'N/A';
  }

  setDimensionRating(dimension: DimensionRating, star: number): void {
    dimension.rating = star;
    this.applyFilters();
  }

  resetDimensionFilters(): void {
    this.filters.find(filter => filter.name === 'dimension')?.dimensions?.forEach(dimension => {
      dimension.rating = 0;
      dimension.hoverRating = 0;
    });
    this.applyFilters();
  }

  resetInclusionIndexFilter(): void {
    const inclusionFilter = this.filters.find(filter => filter.name === 'inclusionIndex');
    if (inclusionFilter) {
      inclusionFilter.value = inclusionFilter.min ?? 1;
      this.applyFilters();
    }
  }

  applyFilters(): void {
    this.currentPage = 1;
    this.expandedCompanyId = null;
    this.reloadCompanies$.next();
  }

  changePage(page: number): void {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.currentPage = page;
      this.expandedCompanyId = null;
      this.reloadCompanies$.next();
    }
  }

  private buildFilters(): CompanyCatalogFilters {
    const dimensionRatings = this.filters.find(filter => filter.name === 'dimension')?.dimensions ?? [];
    const rating = (name: string): number | undefined => {
      const value = dimensionRatings.find(dimension => dimension.name === name)?.rating ?? 0;
      return value > 0 ? value : undefined;
    };
    const inclusionIndex = this.filters.find(filter => filter.name === 'inclusionIndex')?.value;
    const cityIds = this.selectedCities.filter(cityId => cityId !== 0);

    return {
      page: this.currentPage - 1,
      search: this.searchTerm.trim() || undefined,
      countryId: this.selectedCountryId ?? undefined,
      cityIds: cityIds.length > 0 ? cityIds : undefined,
      sectorId: this.selectedSectorId ?? undefined,
      badge: this.badgeFilter || undefined,
      minInclusionIndex: inclusionIndex,
      minLeadershipScore: rating('leadership'),
      minIntegrationScore: rating('bintegration'),
      minMentorshipScore: rating('mentorship'),
      minCulturalScore: rating('cultural'),
      minOutcomeScore: rating('outcome')
    };
  }

  loadCompanyDetails(id: number): void {
    this.allService.getCompanyFullDetails(id).subscribe({
      error: error => console.error('Errore durante il caricamento dei dettagli:', error)
    });
  }

  goToAllCompanies(): void {
    this.router.navigate(['/all-companies']);
  }

  toggleCompanyExpansion(id: number): void {
    this.expandedCompanyId = this.expandedCompanyId === id ? null : id;
  }

  getColor(value: number | undefined): string {
    if (value == null) return 'transparent';
    if (value >= 1 && value <= 1.9) return 'red';
    if (value >= 2 && value <= 2.9) return '#d99c32';
    if (value >= 3 && value <= 3.9) return 'blue';
    if (value >= 4 && value <= 5) return 'green';
    return 'transparent';
  }

  goToHome(): void {
    this.router.navigate(['/home']);
  }

  openCompanyModal(company: CompanyInfo): void {
    this.dialog.open(CompanyModalComponent, {
      maxWidth: '1100px',
      maxHeight: '90vh',
      panelClass: 'company-modal-panel',
      data: company
    });
  }

  badgeCheckFilter(): void {
    this.badgeFilter = !this.badgeFilter;
    this.applyFilters();
  }

  onFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement)?.files?.[0];
    if (!file) {
      this.error = 'Nessun file selezionato';
      return;
    }
    this.selectedFile = file;
    this.error = null;
  }

  uploadLogo(): void {
    if (!this.selectedFile) {
      this.error = 'Seleziona un file prima di caricare.';
      return;
    }
    this.allService.uploadLogo(this.companyId, this.selectedFile).subscribe({
      next: base64 => {
        this.logoBase64 = base64;
        this.selectedFile = null;
      },
      error: () => this.error = 'Errore nel caricamento del logo.'
    });
  }
}
